import * as Sentry from '@sentry/browser';

import { config } from 'src/config/config';
import { appClient } from 'src/config/apollo';
import { toastNotificationsService, userService, benefitsService } from 'src/config/rootService';
import { RootState } from 'src/config/rootStore';
import {
    MemberProfileInput,
    CareRecommendationNode,
    CareRecommendationState,
} from 'src/generated/webapp_gql';
import {
    UpdateMemberProfileDocument,
    UpdateMemberProfileMutation,
    UpdateMemberProfileMutationVariables,
} from 'src/modules/user/documents/updateMemberProfileMutation.generated';

import {
    CleverTapProxyNotificationCategories,
    Role,
    SharedPreference,
    SOURCE_TYPES,
    UserDetails,
    UserState,
} from '../models/user';
import { getUserId } from '../selectors/userSelectors';

export const defaultState: UserState = {
    avatar: '',
    company: '',
    companyUuid: '',
    companyBenefitsPageSubheading: '',
    companySsoOnly: false,
    email: '',
    firstName: '',
    birthDate: '',
    id: -1,
    uuid: '',
    lastName: '',
    state: '',
    localTimezone: '',
    username: '',
    zipCode: '',
    mailingAddress: '',
    phoneNumber: '',
    newEmailAddressNotConfirmed: '',
    availability: [],
    city: '',
    country: '',
    parentUuid: null,
    isDependent: false,
    languagePreferences: [],
    mobileAppUsed: false,
    role: Role.MEMBER,
    defaultVisitDuration: 0,
    companyAdditionalBenefits: [],
    careRecommendations: [],
    eligiblePersonEmail: null,
    isActiveCobraRifMember: false,
    emailConfirmed: false,
    providerCertifications: [],
    joinedAt: '',
    crisisProviderCompanyAssignments: [],
    crisisProviderCountryAssignments: [],
    registrationSource: '',
    hpiEligibilityStatus: false,
    hpiMemberInsurance: null,
    featuredBenefits: [],
    isCompanyHpiEnabled: false,
    isCompanyPsychiatryEnabled: false,
    sharedPreferences: [],
    notificationPreferences: {
        [CleverTapProxyNotificationCategories.DailyPauseRemindersEmail]: false,
    },
    showCrisisModal: false,
    crisisModalSource: SOURCE_TYPES.HOME_PAGE,
    ssoOnly: false,
};

interface UpdateUserInfoPayload extends UserDetails {
    showNotification?: string;
}

export const user = {
    state: defaultState,
    reducers: {
        saveUserInfo: (state: UserState, payload: Partial<UserDetails>): UserState => {
            // TODO: Firebase Analytics Removal
            // ReactGA.set({ userId: payload.uuid });
            // ReactGA.set({ dimension1: payload.company });

            return {
                ...state,
                ...payload,
            };
        },
        addCareRecommendation: (state: UserState, payload: CareRecommendationNode): UserState => {
            const newState = {
                ...state,
                careRecommendations: [payload, ...state.careRecommendations],
            };

            return newState;
        },
        markCareRecommendationAsBypassed: (
            state: UserState,
            careRecommendationUuid: string,
        ): UserState => {
            const newState = {
                ...state,
                careRecommendations: state.careRecommendations.map(careRec => {
                    if (careRec.uuid !== careRecommendationUuid) {
                        return careRec;
                    }

                    return {
                        ...careRec,
                        state: CareRecommendationState.BypassedAssessments,
                    };
                }),
            };

            return newState;
        },
        updateSharedPreferences: (state: UserState, payload: SharedPreference): UserState => {
            const preferenceIndexToUpdate = state.sharedPreferences.findIndex(
                preference => preference.key === payload.key,
            );

            if (preferenceIndexToUpdate >= 0) {
                state.sharedPreferences[preferenceIndexToUpdate].value = payload.value;
            } else {
                state.sharedPreferences = [...state.sharedPreferences, payload];
            }

            return state;
        },
        'authentication/logout': (): UserState => {
            return defaultState;
        },
        toogleShowCrisisModal: (state: UserState): UserState => {
            const newState = {
                ...state,
                showCrisisModal: !state.showCrisisModal,
            };
            return newState;
        },
        updateCrisisModalSource: (state: UserState, payload: SOURCE_TYPES): UserState => {
            const newState = {
                ...state,
                crisisModalSource: payload,
            };
            return newState;
        },
    },
    effects: dispatch => ({
        async fetchUserInfo(): Promise<UserDetails> {
            const response = await userService.fetchUserInfo();

            const { sessionInfo, userDetails } = response;

            dispatch.user.saveUserInfo(userDetails);
            Sentry.configureScope(scope => {
                scope.setUser({ id: `${userDetails.id}` });
            });

            dispatch.messaging.setActiveUserUuid(userDetails.uuid);
            dispatch.videoSessions.addSessionUtilizationInfo(sessionInfo);

            const companyAdditionalBenefits =
                await benefitsService.fetchCompanyAdditionalBenefits();

            if (companyAdditionalBenefits) {
                dispatch.user.saveUserInfo({ companyAdditionalBenefits });
            }

            const crisisProviderCompanyAssignments =
                await userService.fetchCrisisProvidersAssignedToCompany();

            if (crisisProviderCompanyAssignments) {
                dispatch.user.saveUserInfo({ crisisProviderCompanyAssignments });
            }

            const crisisProviderCountryAssignments =
                await userService.fetchCrisisProvidersAssignedToCountry();

            if (crisisProviderCountryAssignments) {
                dispatch.user.saveUserInfo({ crisisProviderCountryAssignments });
            }

            const [hpiEligibilityStatus, hpiMemberInsurance] = await Promise.all([
                userService.fetchHPIEligibilityStatus(),
                userService.fetchHPIMemberInsurance(),
            ]);

            if (hpiEligibilityStatus) {
                dispatch.user.saveUserInfo({ hpiEligibilityStatus });
            }
            if (hpiMemberInsurance) {
                dispatch.user.saveUserInfo({ hpiMemberInsurance });
            }

            return userDetails;
        },
        async updateEmailAddress(emailAddress: string, state: RootState): Promise<void> {
            const userId = getUserId(state);

            const data = await userService.updateEmailAddress(emailAddress, userId);

            if (data) {
                dispatch.user.saveUserInfo(data);
            }
        },
        async updateMemberProfile(payload: MemberProfileInput, state: RootState) {
            const { data, errors } = await appClient.mutate<
                UpdateMemberProfileMutation,
                UpdateMemberProfileMutationVariables
            >({
                mutation: UpdateMemberProfileDocument,
                variables: { memberProfile: payload },
            });

            if (!data) {
                throw errors;
            }

            const languagePreferenceChanged =
                payload.languagePreferences?.[0] !== state.user.languagePreferences[0];

            dispatch.user.saveUserInfo(data.updateMemberProfile?.member);

            if (languagePreferenceChanged) {
                dispatch.assessments.fetchAssessments();
                dispatch.countries.fetchCountries();
                dispatch.languages.fetchLanguages();
                dispatch.goalCategories.fetchGoalsCategories();
                dispatch.providers.fetchMyProviders();
                dispatch.user.fetchUserInfo();
            }
        },
        /**
         * Replaced by updateMemberProfile
         * @deprecated
         *
         * DO NOT USE FOR NEW FEATURES
         *
         * The following flows are still relying on this:
         * - PostAuth
         * - PreferredEmailPage
         * - RegistrationPage
         * - CrisisModal
         *
         * Because there is no type safety in those files with our
         * dispatch type and useDispatch, refactoring those usages needs
         * to be done carefully with thorough regression testing.
         * For this reason those components were not migrated when the
         * updateMemberProfile mutation was introduced.
         */
        async updateUserInfo(payload: UpdateUserInfoPayload, state: RootState): Promise<void> {
            const id = getUserId(state);

            try {
                const data = await userService.updateUserInfo(payload, id);
                const languagePreferenceChanged =
                    payload.languagePreferences[0] !== state.user.languagePreferences[0];

                if (data) {
                    dispatch.user.saveUserInfo(data);

                    if (languagePreferenceChanged) {
                        dispatch.assessments.fetchAssessments();
                        dispatch.countries.fetchCountries();
                        dispatch.languages.fetchLanguages();
                        dispatch.goalCategories.fetchGoalsCategories();
                        dispatch.providers.fetchMyProviders();
                        dispatch.user.fetchUserInfo();
                    }

                    if (!payload.showNotification) {
                        return;
                    }

                    const message =
                        payload.email === data.newEmailAddressNotConfirmed
                            ? config.messages.userInfoEmailUpdated
                            : config.messages.userInfoUpdateSuccess;

                    toastNotificationsService.renderSuccessToast(message);
                }
            } catch {
                // error notification will be spawned from the service
                // additional error handling can be added here
            }
        },
    }),
};
